import m from "mithril";
import { getMithril } from "../utils.js"; // Standardized Mithril request utilities
import showToast from "../js/toaster.js";

var CoordCustomers = {
    oninit: function(vnode) {
        this.customers = [];
        this.isLoading = true;
        this.error = null;
        this.searchTerm = "";
        this.currentPage = 1;
        this.itemsPerPage = 10;
        this.pagination = {
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
        };

        this.fetchCustomers();
    },

    fetchCustomers: async function(page = 1) {
        try {
            this.currentPage = page;
            this.isLoading = true;

            const params = new URLSearchParams({
                page: page.toString(),
                limit: this.itemsPerPage.toString()
            });

            if (this.searchTerm) {
                params.append('search', this.searchTerm);
            }

            const response = await getMithril(`/api/customers?${params.toString()}`, { component: this });
            
            if (response.success) {
                this.customers = response.data || [];
                this.pagination = response.pagination || {
                    total: 0,
                    totalPages: 0,
                    hasNext: false,
                    hasPrev: false
                };
            } else {
                throw new Error(response.error || "Gagal memuat data nasabah");
            }
        } catch (error) {
            console.error("Error fetching customers:", error);
            this.error = error.message;
            showToast("Gagal memuat data nasabah: " + error.message, "error");
        } finally {
            this.isLoading = false;
            m.redraw();
        }
    },

    search: function() {
        // Reset to first page when searching
        this.currentPage = 1;
        this.fetchCustomers(1);
    },

    goToPage: function(page) {
        if (page >= 1 && page <= this.pagination.totalPages) {
            this.fetchCustomers(page);
        }
    },

    viewCustomer: function(customerId) {
        // Show notification instead of confirmation dialog
        showToast("Membuka detail nasabah...", "info");
        // Navigate to customer detail view
        m.route.set(`/view-customer/${customerId}`);
    },

    view: function(vnode) {
        return m("div", { class: "p-6" }, [
            m("div", { class: "mb-6" }, [
                m("h2", { class: "text-2xl font-bold text-gray-800 dark:text-white" }, "Data Nasabah"),
                m("p", { class: "text-gray-600 dark:text-gray-300" }, "Daftar nasabah (mode baca-saja untuk koordinator)")
            ]),

            // Search section
            m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6" }, [
                m("div", { class: "flex flex-col md:flex-row md:items-center md:justify-between gap-4" }, [
                    m("div", { class: "flex-1" }, [
                        m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Cari Nasabah"),
                        m("div", { class: "relative" }, [
                            m("input", {
                                type: "text",
                                placeholder: "Cari berdasarkan nama, nomor KTP, atau nomor telepon...",
                                class: "w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white",
                                value: this.searchTerm,
                                oninput: (e) => {
                                    this.searchTerm = e.target.value;
                                },
                                onkeypress: (e) => {
                                    if (e.key === "Enter") {
                                        this.search();
                                    }
                                }
                            }),
                            m("div", { class: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" }, [
                                m("i", { class: "ri-search-line text-gray-400" })
                            ])
                        ])
                    ]),
                    m("button", {
                        class: "px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center transition-colors",
                        onclick: () => this.search()
                    }, [
                        m("i", { class: "ri-search-line mr-2" }),
                        "Cari"
                    ])
                ])
            ]),

            // Error message
            this.error && !this.isLoading ? m("div", { class: "mb-6 bg-red-50 dark:bg-red-900 border-l-4 border-red-500 p-4 rounded" }, [
                m("div", { class: "flex items-center" }, [
                    m("i", { class: "ri-error-warning-line text-red-500 dark:text-red-300 text-xl mr-3" }),
                    m("div", [
                        m("p", { class: "text-red-700 dark:text-red-200 font-medium" }, "Gagal Memuat Data"),
                        m("p", { class: "text-red-600 dark:text-red-300 text-sm mt-1" }, this.error)
                    ])
                ]),
                m("button", {
                    class: "mt-3 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors duration-200",
                    onclick: () => this.fetchCustomers()
                }, "Coba Lagi")
            ]) : null,

            // Loading indicator
            this.isLoading ? m("div", { class: "flex justify-center items-center h-64" }, [
                m("div", { class: "text-center" }, [
                    m("i", { class: "ri-loader-4-line animate-spin text-3xl text-orange-500 mb-4 block" }),
                    m("p", { class: "text-gray-600 dark:text-gray-300" }, "Memuat data nasabah...")
                ])
            ]) : null,

            // Customers table
            !this.isLoading && !this.error ? m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden" }, [
                m("div", { class: "overflow-x-auto" }, [
                    m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                        m("thead", { class: "bg-gray-50 dark:bg-gray-700" }, [
                            m("tr", [
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Nama Nasabah"),
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "No KTP"),
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "No Telepon"),
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Alamat"),
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Subunit"),
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Actions")
                            ])
                        ]),
                        m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" }, [
                            this.customers.length === 0 ? 
                                m("tr", [
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center", colspan: "6" }, "Tidak ada data nasabah")
                                ]) : 
                                this.customers.map(customer => {
                                     const isGroupCustomer = customer.customer_type === 'kelompok';
                                     const rowClass = `hover:bg-gray-50 dark:hover:bg-gray-750 ${isGroupCustomer ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500 dark:border-l-blue-400' : ''}`;

                                     return m("tr", { class: rowClass }, [
                                         m("td", { class: `px-6 py-4 whitespace-nowrap text-sm font-medium ${isGroupCustomer ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'}` }, [
                                             isGroupCustomer ? m("i", { class: "ri-group-line mr-2 text-blue-600 dark:text-blue-400" }) : null,
                                             customer.nama_nasabah,
                                             isGroupCustomer ? m("span", { class: "ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200" }, "Kelompok") : null
                                         ]),
                                         m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" },
                                             isGroupCustomer ? m("span", { class: "text-gray-400 dark:text-gray-500 italic" }, "N/A") : (customer.no_ktp || "-")
                                         ),
                                         m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" },
                                             isGroupCustomer ? m("span", { class: "text-gray-400 dark:text-gray-500 italic" }, "N/A") : (customer.no_telepon || "-")
                                         ),
                                         m("td", { class: "px-6 py-4 text-sm text-gray-500 dark:text-gray-400" },
                                             isGroupCustomer ?
                                                 m("span", { class: "text-gray-400 dark:text-gray-500 italic" }, "Data kelompok tersedia di detail") :
                                                 `${customer.alamat || ""}${customer.rt || customer.rw ? `, RT${customer.rt || ""}/RW${customer.rw || ""}` : ""}`
                                         ),
                                         m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" }, customer.subunit_name || "-"),
                                         m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium" }, [
                                             m("button", {
                                                 class: `mr-3 ${isGroupCustomer ? 'text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300' : 'text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300'}`,
                                                 onclick: () => this.viewCustomer(customer.id)
                                             }, [
                                                 m("i", { class: "ri-eye-line mr-1" }),
                                                 "Lihat"
                                             ])
                                         ])
                                     ])
                                 })
                        ])
                    ])
                ]),

                // Pagination
                this.pagination.totalPages > 1 ? m("div", { class: "bg-white dark:bg-gray-800 px-6 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700" }, [
                    m("div", { class: "flex-1 flex justify-between sm:hidden" }, [
                        m("button", {
                            class: "relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600",
                            disabled: !this.pagination.hasPrev,
                            onclick: () => this.goToPage(this.currentPage - 1)
                        }, "Sebelumnya"),
                        m("button", {
                            class: "ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600",
                            disabled: !this.pagination.hasNext,
                            onclick: () => this.goToPage(this.currentPage + 1)
                        }, "Berikutnya")
                    ]),
                    m("div", { class: "hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" }, [
                        m("div", [
                            m("p", { class: "text-sm text-gray-700 dark:text-gray-300" }, [
                                "Menampilkan ",
                                m("span", { class: "font-medium" }, ((this.currentPage - 1) * this.itemsPerPage) + 1),
                                " hingga ",
                                m("span", { class: "font-medium" }, Math.min(this.currentPage * this.itemsPerPage, this.pagination.total)),
                                " dari ",
                                m("span", { class: "font-medium" }, this.pagination.total),
                                " hasil"
                            ])
                        ]),
                        m("div", [
                            m("nav", { class: "relative z-0 inline-flex rounded-md shadow-sm -space-x-px", "aria-label": "Pagination" }, [
                                m("button", {
                                    class: "relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600",
                                    disabled: !this.pagination.hasPrev,
                                    onclick: () => this.goToPage(this.currentPage - 1)
                                }, [
                                    m("i", { class: "ri-arrow-left-s-line" })
                                ]),
                                // Page numbers
                                Array.from({ length: Math.min(5, this.pagination.totalPages) }, (_, i) => {
                                    let page;
                                    if (this.pagination.totalPages <= 5) {
                                        page = i + 1;
                                    } else if (this.currentPage <= 3) {
                                        page = i + 1;
                                    } else if (this.currentPage >= this.pagination.totalPages - 2) {
                                        page = this.pagination.totalPages - 4 + i;
                                    } else {
                                        page = this.currentPage - 2 + i;
                                    }

                                    return m("button", {
                                        class: `relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                            this.currentPage === page 
                                                ? 'z-10 bg-orange-50 dark:bg-orange-900 border-orange-500 text-orange-600 dark:text-orange-300' 
                                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                                        }`,
                                        onclick: () => this.goToPage(page)
                                    }, page);
                                }),
                                m("button", {
                                    class: "relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600",
                                    disabled: !this.pagination.hasNext,
                                    onclick: () => this.goToPage(this.currentPage + 1)
                                }, [
                                    m("i", { class: "ri-arrow-right-s-line" })
                                ])
                            ])
                        ])
                    ])
                ]) : null
            ]) : null
        ]);
    }
};

export default CoordCustomers;